import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar from '@/components/feature/FilterBar';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/feature/Pagination';
import { usePageMeta } from '@/hooks/usePageMeta';
import { categories } from '@/mocks/categories';
import { queryMovies, type DecadeFilter, type SortFilter } from '@/utils/movies';

const BASE_PER_PAGE = 12;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();

  const genreParam = searchParams.get('genre') ?? 'all';
  const sortParam = (searchParams.get('sort') as SortFilter) ?? 'latest';

  const [genre, setGenre] = useState(genreParam);
  const [decade, setDecade] = useState<DecadeFilter>('all');
  const [sort, setSort] = useState<SortFilter>(sortParam);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(BASE_PER_PAGE);

  useEffect(() => {
    setGenre(genreParam);
    setSort(sortParam);
    setPage(1);
    setPerPage(BASE_PER_PAGE);
  }, [genreParam, sortParam]);

  const activeCategory = categories.find((item) => item.slug === genre);

  const title = activeCategory ? `${activeCategory.name} - 人间中毒在线` : '电影分类 - 人间中毒在线';
  const description = activeCategory
    ? `${activeCategory.description} 在人间中毒在线浏览全部${activeCategory.name}电影，查看剧情介绍与观影推荐。`
    : '人间中毒在线电影分类页，按类型、年代与热度筛选，快速找到你想看的电影。';

  usePageMeta({
    title,
    description,
    keywords: `人间中毒在线,电影分类,${activeCategory ? activeCategory.name + '电影' : '影视资源'},剧情介绍`,
  });

  const filtered = useMemo(
    () => queryMovies({ genre, decade, sort }),
    [genre, decade, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const hasMore = currentPage * perPage < filtered.length;

  const updateParams = (nextGenre: string, nextSort: SortFilter) => {
    const params: Record<string, string> = {};
    if (nextGenre !== 'all') params.genre = nextGenre;
    if (nextSort !== 'latest') params.sort = nextSort;
    setSearchParams(params, { replace: true });
  };

  const handleGenreChange = (value: string) => {
    setGenre(value);
    setPage(1);
    setPerPage(BASE_PER_PAGE);
    updateParams(value, sort);
  };

  const handleSortChange = (value: SortFilter) => {
    setSort(value);
    setPage(1);
    setPerPage(BASE_PER_PAGE);
    updateParams(genre, value);
  };

  const handleDecadeChange = (value: DecadeFilter) => {
    setDecade(value);
    setPage(1);
    setPerPage(BASE_PER_PAGE);
  };

  const handlePageChange = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '电影分类', to: '/category' },
                ...(activeCategory ? [{ label: activeCategory.name }] : []),
              ]}
            />
            <h1 className="text-2xl font-extrabold text-foreground-950 md:text-3xl">
              {activeCategory ? `${activeCategory.name}电影` : '全部电影'}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
              {activeCategory ? activeCategory.description : '浏览全部收录影片，可按类型、年代与热度自由筛选排序，找到你的下一部心头好。'}
            </p>
            <p className="mt-3 text-sm text-foreground-500">
              共筛选出 <span className="font-semibold text-accent-700">{filtered.length}</span> 部影片
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <FilterBar
            genre={genre}
            decade={decade}
            sort={sort}
            onGenreChange={handleGenreChange}
            onDecadeChange={handleDecadeChange}
            onSortChange={handleSortChange}
          />

          {paged.length > 0 ? (
            <>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {paged.map((movie, i) => (
                  <div key={movie.id} className={`anim-fade-up delay-${(i % 5) + 1}`}>
                    <MovieCard movie={movie} />
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setPerPage((prev) => prev + BASE_PER_PAGE)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-6 py-2.5 text-sm font-medium text-foreground-900 transition hover:border-accent-400 hover:text-accent-700 cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                    加载更多
                  </button>
                </div>
              )}

              <Pagination page={currentPage} totalPages={totalPages} onChange={handlePageChange} />
            </>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-film-line text-3xl"></i>
              </span>
              <h2 className="mt-5 text-lg font-bold text-foreground-950">该条件下暂无影片</h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                试试放宽筛选条件，或返回查看全部影片，也许会有意外惊喜。
              </p>
              <button
                type="button"
                onClick={() => {
                  setDecade('all');
                  handleGenreChange('all');
                }}
                className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-accent-600 dark:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          )}

          <div className="mt-10 flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <Link
                key={category.slug}
                to={`/category?genre=${encodeURIComponent(category.slug)}`}
                className={`rounded-full border px-4 py-1.5 text-sm transition ${
                  category.slug === genre
                    ? 'border-accent-500 bg-accent-500 text-background-50 dark:text-foreground-950'
                    : 'border-background-200 bg-background-100 text-foreground-700 hover:border-accent-400 hover:text-accent-700'
                }`}
              >
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}